import { history } from '../../../store';
import { importSessionState as importShorelineSession } from '../../../algorithms/shoreline/sessionExport';
import { importSessionState as importBathymetrySession } from '../../../algorithms/bathymetry/sessionExport';
import { importSessionState as importCSqueezeSession } from '../../../algorithms/csqueeze/sessionExport';
import { importSessionState as importEUSCoastSession } from '../../../algorithms/euscoast/sessionExport';
import { importSessionState as importIntertidalSession } from '../../../algorithms/intertidal/sessionExport';
import { importSessionState as importMepBaySession } from '../../../algorithms/mepbay/sessionExport';
import { importSessionState as importShoreRSession } from '../../../algorithms/shoreR/sessionExport';
import { importSessionState as importShoreSSession } from '../../../algorithms/shoreS/sessionExport';
import { ACQUISITION_MODULE_KEY, ALL_STEPS_REACHED, FURTHEST_STEP_KEY_PREFIX } from '../../../hooks/useStepNavigations';
import { markSessionImported } from '../../../common/utils';

/**
 * @function buildModuleImportConfigs
 * @description Builds the module-key -> {module, onImport} map used to replay a
 * `buildSessionExport`-produced JSON snapshot for any of the 8 modules: `onImport` rehydrates
 * Redux state via that module's own `importSessionState` and navigates to its result page.
 * Shared between `NavigationBar` (per-module import, already inside that module) and
 * `SelectionPage` (generic import, routes based on the file's own `module` field) so the
 * module -> route/importer mapping isn't duplicated.
 * @param {Object} deps
 * @param {Function} deps.dispatch - Redux dispatch.
 * @param {Function} deps.setData - `useResult()`'s setData, needed by bathymetry's importer.
 * @param {Function} deps.setFileData - `useFile()`'s setFileData, needed by bathymetry's importer.
 * @param {Function} deps.saveProcessingCollectionValues - From `usePreProcessing()`, needed by bathymetry's importer.
 * @param {Object} deps.waterMaskMethodRef - From `usePreProcessing()`, needed by bathymetry's importer.
 * @param {Function} deps.generateCollection - From `usePreProcessing()`, needed by bathymetry's importer.
 * @param {Function} deps.cleanAndMaskImages - From `usePreProcessing()`, needed by bathymetry's importer.
 * @param {Function} deps.createMosaicByCommonId - From `usePreProcessing()`, needed by bathymetry's importer.
 * @param {Object} deps.collectionRef - From `usePreProcessing()`, needed by bathymetry's importer.
 * **Called by:
 * @see {buildSessionImportConfigs} - which wraps each entry before handing the map out.
 * @memberof module:SHARED
 * @returns {Object} Module key -> {module: String, onImport: Function}.
 */
const buildModuleImportConfigs = ({
  dispatch,
  setData,
  setFileData,
  saveProcessingCollectionValues,
  waterMaskMethodRef,
  generateCollection,
  cleanAndMaskImages,
  createMosaicByCommonId,
  collectionRef,
}) => ({
  shoreline: {
    module: 'shoreline',
    onImport: async (data) => {
      await importShorelineSession(dispatch, data);
      history.push('/main/processing');
    },
  },
  bathymetry: {
    module: 'bathymetry',
    onImport: async (data) => {
      await importBathymetrySession(
        {
          dispatch,
          saveProcessingCollectionValues,
          waterMaskMethodRef,
          generateCollection,
          cleanAndMaskImages,
          createMosaicByCommonId,
          collectionRef,
          setData,
          setFileData,
        },
        data,
      );
      history.push('/main/bathymetry/result');
    },
  },
  csqueeze: {
    module: 'csqueeze',
    onImport: async (data) => {
      await importCSqueezeSession(dispatch, data);
      history.push('/main/csqueeze/result');
    },
  },
  euscoast: {
    module: 'euscoast',
    onImport: async (data) => {
      await importEUSCoastSession(dispatch, data);
      history.push('/main/euscoast/result');
    },
  },
  intertidal: {
    module: 'intertidal',
    onImport: async (data) => {
      await importIntertidalSession(dispatch, data);
      history.push('/main/intertidal/result');
    },
  },
  mepbay: {
    module: 'mepbay',
    onImport: async (data) => {
      await importMepBaySession(dispatch, data);
      history.push('/main/mepbay/result');
    },
  },
  shore_r: {
    module: 'shore_r',
    onImport: async (data) => {
      await importShoreRSession(dispatch, data);
      history.push('/main/processing_shore_r');
    },
  },
  shore_s: {
    module: 'shore_s',
    onImport: (data) => {
      importShoreSSession(dispatch, data);
      history.push('/main/processing_shore_s');
    },
  },
});

/**
 * @function buildSessionImportConfigs
 * @description Wraps every module's importer so it also records three things about the result page
 * it is on its way to, none of which the page itself can work out once it gets there.
 * Which module the acquisition state now belongs to: the wizard keeps that marker to tell a module
 * switch from a reload, and clears the shared state when it finds another module's name there. An
 * import writes that same shared state from outside any wizard - the generic entry point on the
 * selection page does it without ever opening one - so without this the marker still named whatever
 * module was last visited, and going back from the result page to the steps wiped the session that
 * had just been imported.
 * That the flow has been walked to its end: the steps are gated on how far the user got as well as
 * on the data, and an imported session reached the result without passing through them.
 * And that the page is being reached by replaying a file, so its guided tour stands down - a
 * walkthrough of a screen that already has a restored analysis on it is noise.
 * @see {@link markSessionImported}
 * @param {Object} deps - Same dependencies the per-module importers need.
 * **Called by:
 * @see {NavigationBar}
 * @see {SelectionSessionImportButton}
 * @memberof module:SHARED
 * @returns {Object} Module key -> `{module, onImport}`.
 */
export const buildSessionImportConfigs = (deps) => {
  const configs = buildModuleImportConfigs(deps);
  return Object.keys(configs).reduce((acc, moduleId) => {
    const config = configs[moduleId];
    acc[moduleId] = {
      ...config,
      onImport: async (data) => {
        window.sessionStorage.setItem(ACQUISITION_MODULE_KEY, moduleId);
        window.sessionStorage.setItem(`${FURTHEST_STEP_KEY_PREFIX}${moduleId}`, ALL_STEPS_REACHED);
        markSessionImported();
        await config.onImport(data);
      },
    };
    return acc;
  }, {});
};
